<template>
  <div class="app_wrapper">
    <EnvTag />
    <b-loading
      :is-full-page="true"
      v-model="isLoading"
      :can-cancel="false"
    ></b-loading>

    <!-- bootError keeps the app unmounted after a failed sign-in: without
         Firebase auth every Firestore read would just fail underneath the
         error dialog. -->
    <div id="app" v-if="!isLoading && !bootError">
      <!-- Student layout -->
      <div v-if="isStudent" class="app__student">
        <!-- Banner -->
        <div>
          <img src="@/assets/img/header.png" alt="Chess Squad Academy" />
        </div>

        <div v-if="isAuthRoute">
          <StudentNavbar />
          <RoomFloating roomId="lobby" />
          <TournamentFloating />
        </div>

        <router-view class="app__container" :key="$route.fullPath" />

        <IdleTimer
          :duration="30 * 60"
          :reminders="[10 * 60]"
          v-if="isAuthRoute"
        />

        <TrackOnlineStatus v-if="isAuthRoute" />
      </div>

      <!-- Teacher layout -->
      <div v-if="isTeacher" class="app__teacher">
        <!-- Banner -->
        <div>
          <img
            src="@/assets/img/header.png"
            alt="Chess Squad Academy"
            style="max-height: 80px"
          />
        </div>

        <TeacherNavbar v-if="isAuthRoute" />
        <TeacherFloating v-if="isAuthRoute" />
        <router-view class="app__container pb-4 mb-1" :key="$route.fullPath" />
      </div>
    </div>
  </div>
</template>

<script>
import EnvTag from "@/components/common/EnvTag.vue";
import TrackOnlineStatus from "@/components/TrackOnlineStatus.vue";
import StudentNavbar from "@/components/navbars/StudentNavbar.vue";
import TeacherNavbar from "@/components/navbars/TeacherNavbar.vue";
import RoomFloating from "@/components/rooms/RoomFloating.vue";
import TeacherFloating from "@/components/teacher/TeacherFloating.vue";
import IdleTimer from "@/components/timers/IdleTimer.vue";
import TournamentFloating from "@/components/tournaments/TournamentFloating.vue";
import {
  initFirebaseAccessToken,
  initFirestoreTimeOffset,
  initRtdbPresence,
} from "@/services/firebase";

export default {
  components: {
    EnvTag,
    StudentNavbar,
    RoomFloating,
    TournamentFloating,
    IdleTimer,
    TrackOnlineStatus,
    TeacherNavbar,
    TeacherFloating,
  },

  data() {
    return {
      isLoading: true,
      bootError: false,
    };
  },

  computed: {
    isAuthRoute() {
      return !this.$route.meta?.skipAuth;
    },

    isStudent() {
      return this.$route.meta?.student ?? true;
    },

    isTeacher() {
      return this.$route.meta?.teacher ?? false;
    },
  },

  async created() {
    // Previously a single failed/hung sign-in left the full-page spinner up
    // forever with no feedback. Retry a few times, then show an error state
    // the user can act on.
    //
    // Each attempt is raced against a 15s timeout: the httpsCallable's own
    // deadline is ~70s, so a connection that stalls (school Wi-Fi that is
    // "connected" with a dead uplink) would otherwise leave the coach staring
    // at the unchanged spinner for over 3 minutes before the error appeared.
    // With the cap, worst case to the error screen is ~51s.
    const MAX_ATTEMPTS = 3;
    const ATTEMPT_TIMEOUT_MS = 15000;

    const signInWithTimeout = () =>
      new Promise((resolve, reject) => {
        const timer = setTimeout(
          () => reject(new Error("Firebase sign-in timed out")),
          ATTEMPT_TIMEOUT_MS
        );
        initFirebaseAccessToken()
          .then(resolve, reject)
          .finally(() => clearTimeout(timer));
      });

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        await signInWithTimeout();
        break;
      } catch (err) {
        console.error(`Firebase sign-in failed (attempt ${attempt}):`, err);
        if (attempt === MAX_ATTEMPTS) {
          this.bootError = true;
          this.isLoading = false;
          this.$buefy.dialog.alert({
            title: "Unable to connect",
            message:
              "We couldn't connect to Chess Squad. Please check your " +
              "internet connection and try again.",
            confirmText: "Retry",
            canCancel: false,
            onConfirm: () => window.location.reload(),
          });
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, attempt * 2000));
      }
    }
    initFirestoreTimeOffset();
    initRtdbPresence();
    this.isLoading = false;
  },
};
</script>

<style scoped>
#app {
  font-family: "Roboto", "Avenir", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  background-color: var(--app-background-color);
  background-image: url("./assets/img/background_tile_3.png");
  background-repeat: repeat;
  background-position: top;
  display: flex;
  flex-direction: column;
  margin-left: auto;
  margin-right: auto;
  height: 100vh;
  place-items: center;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Thin scrollbar for IE, Edge, Firefox */
* {
  -ms-overflow-style: auto; /* IE and Edge */
  scrollbar-width: thin; /* Firefox */
}

/* Hide scrollbar for Chrome, Safari */
*::-webkit-scrollbar {
  width: 5px;
}

*::-webkit-scrollbar-track {
  background: none;
}

*::-webkit-scrollbar-thumb {
  background-color: grey;
  border-radius: 5px;
  /* border: 3px solid orange; */
}

.app__student {
  max-width: 1370px;
  width: 98vw;
}

.app__teacher {
  max-width: 1570px;
  width: 98vw;
}

.app__container {
  background-color: white;
  width: 100%;
  min-height: 80vh;
  padding: 1rem;
  padding-bottom: 4rem;
  margin-bottom: 4rem;
  position: relative;
}

.app__dev {
  text-align: right;
  color: grey;
  font-size: 12px;
  position: absolute;
  bottom: 0;
  right: 0;
}

.center {
  margin-left: auto;
  margin-right: auto;
}
</style>
